import { UserKibsiClient } from '@kibsi/ks-client-sdk'
import { SelfPasswordUpdate, SelfUserUpdate, User, UserCreate, UserUpdate } from '@kibsi/ks-tenant-types'
import { inject, injectable } from 'inversify'
import TYPES from '../../config/inversify.types'
import { RequestStore } from '../../store/request.store'
import { UserService } from './user.service'

export type UserApi = Pick<
    InstanceType<typeof UserKibsiClient>,
    | 'list'
    | 'read'
    | 'getSelf'
    | 'updateSelf'
    | 'updateSelfPicture'
    | 'setSelfPassword'
    | 'create'
    | 'update'
    | 'delete'
    | 'getProfilePictureUrl'
>

@injectable()
export class UserServiceImpl implements UserService {
    constructor(@inject(TYPES.RequestStore) private req: RequestStore, @inject(TYPES.UserApi) private api: UserApi) {}

    async list(): Promise<User[]> {
        return this.req.once('user.list', async () => {
            const { results } = await this.api.list({ pageSize: 500 })

            return results
        })
    }

    get(id: string): Promise<User> {
        return this.req.once(`site.get.${id}`, () => this.api.read(id))
    }

    getSelf(): Promise<User> {
        return this.req.once(`site.get.self`, () => this.api.getSelf())
    }

    getProfilePictureUrl(userId: string): Promise<string> {
        return this.req.once(`user.${userId}.picture`, () =>
            this.api.getProfilePictureUrl(userId).catch((reason: any) => {
                if (reason.statusCode === 404 && reason.errorCode === 'ENTITY_NOT_FOUND') {
                    // returning a string, so that the image element will not error out due to undefined src url
                    // also this isn't an error error, its a expected error. And we don't want this in our error logs
                    return '//:0'
                }

                throw reason
            }),
        )
    }

    updateSelf(user: SelfUserUpdate): Promise<void> {
        return this.api.updateSelf(user)
    }

    updateSelfPicture(buffer: Buffer): Promise<void> {
        return this.api.updateSelfPicture(buffer)
    }

    updateSelfPassword(user: SelfPasswordUpdate): Promise<void> {
        return this.api.setSelfPassword(user)
    }

    create(user: UserCreate): Promise<User> {
        return this.api.create(user)
    }

    update(user: UserUpdate): Promise<void> {
        return this.api.update(user)
    }

    delete(userId: string): Promise<void> {
        return this.api.delete({ userId } as User)
    }
}
